'use client';

import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import {
  Sparkles,
  Send,
  CheckCircle,
  ArrowLeft,
  Loader2,
  Building2,
  Users,
  Wrench,
  ChevronDown,
  Check,
} from 'lucide-react';
import Link from 'next/link';
import { captureClientError } from '@/lib/error-reporting/client';

// =============================================================================
// CONSTANTS
// =============================================================================

const COMPANY_SIZES = [
  { value: '1', label: 'Just me', icon: '👤' },
  { value: '2-10', label: '2-10 employees', icon: '👥' },
  { value: '11-50', label: '11-50 employees', icon: '🏢' },
  { value: '51-200', label: '51-200 employees', icon: '🏬' },
  { value: '200+', label: '200+ employees', icon: '🌆' },
];

const BUDGET_RANGES = [
  { value: 'not-sure', label: 'Not sure yet', icon: '🤔' },
  { value: '500-1000', label: '€500 - €1,000/month', icon: '💰' },
  { value: '1000-2500', label: '€1,000 - €2,500/month', icon: '💎' },
  { value: '2500-5000', label: '€2,500 - €5,000/month', icon: '🚀' },
  { value: '5000+', label: '€5,000+/month', icon: '⭐' },
];

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  companySize: string;
  currentTools: string;
  budgetRange: string;
  message: string;
}

// =============================================================================
// HOOKS
// =============================================================================

function useScrollReveal(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

// =============================================================================
// COMPONENTS
// =============================================================================

function ScrollReveal({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const { ref, isVisible } = useScrollReveal();

  return (
    <div
      ref={ref}
      className={cn(
        'opacity-0 translate-y-8 transition-all duration-700 ease-out',
        isVisible && 'opacity-100 translate-y-0',
        className
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

interface GlassCardProps {
  children: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
  title?: string;
}

function GlassCard({ children, className, icon, title }: GlassCardProps) {
  return (
    <div
      className={cn(
        'group relative p-8 rounded-[32px] overflow-hidden',
        'border border-white/5 hover:border-amber-500/20',
        'bg-[#0a0a0a]',
        'transition-all duration-500',
        'hover:shadow-[0_20px_60px_rgba(0,0,0,0.5)]',
        className
      )}
    >
      {/* Background glow on hover */}
      <div
        className={cn(
          'absolute inset-0 opacity-0 group-hover:opacity-100',
          'bg-gradient-to-br from-amber-500/5 via-transparent to-transparent',
          'transition-opacity duration-500'
        )}
      />

      {/* Content */}
      <div className="relative z-10">
        {(icon || title) && (
          <div className="flex items-center gap-3 mb-6">
            {icon && (
              <div className="p-3 rounded-xl bg-black/40 border border-white/5 text-amber-500 group-hover:bg-amber-500/10 group-hover:border-amber-500/20 transition-all duration-300">
                {icon}
              </div>
            )}
            {title && <h2 className="text-lg font-semibold text-white">{title}</h2>}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

interface GlassInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  required?: boolean;
}

function GlassInput({ label, required, className, ...props }: GlassInputProps) {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="relative">
      <label className="block text-sm text-gray-400 mb-2 font-light">
        {label} {required && <span className="text-amber-500">*</span>}
      </label>
      <div className="relative">
        <input
          {...props}
          required={required}
          onFocus={(e) => {
            setIsFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            props.onBlur?.(e);
          }}
          className={cn(
            'w-full px-4 py-3.5 rounded-xl',
            'bg-black/40 border border-white/10',
            'text-white placeholder-gray-600',
            'focus:outline-none focus:border-amber-500/50',
            'transition-all duration-300',
            isFocused && 'shadow-[0_0_20px_rgba(245,158,11,0.15)]',
            className
          )}
        />
        {/* Focus glow effect */}
        <div
          className={cn(
            'absolute inset-0 rounded-xl pointer-events-none',
            'bg-gradient-to-r from-amber-500/10 to-transparent',
            'opacity-0 transition-opacity duration-300',
            isFocused && 'opacity-100'
          )}
        />
      </div>
    </div>
  );
}

interface GlassTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  required?: boolean;
}

function GlassTextarea({ label, required, className, ...props }: GlassTextareaProps) {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="relative">
      <label className="block text-sm text-gray-400 mb-2 font-light">
        {label} {required && <span className="text-amber-500">*</span>}
      </label>
      <div className="relative">
        <textarea
          {...props}
          required={required}
          onFocus={(e) => {
            setIsFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            props.onBlur?.(e);
          }}
          className={cn(
            'w-full px-4 py-3.5 rounded-xl resize-none',
            'bg-black/40 border border-white/10',
            'text-white placeholder-gray-600',
            'focus:outline-none focus:border-amber-500/50',
            'transition-all duration-300',
            isFocused && 'shadow-[0_0_20px_rgba(245,158,11,0.15)]',
            className
          )}
        />
      </div>
    </div>
  );
}

interface GlassSelectProps {
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string; icon?: string }[];
  placeholder: string;
}

function GlassSelect({ label, required, value, onChange, options, placeholder }: GlassSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsFocused(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <label className="block text-sm text-gray-400 mb-2 font-light">
        {label} {required && <span className="text-amber-500">*</span>}
      </label>
      <button
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
          setIsFocused(true);
        }}
        className={cn(
          'w-full px-4 py-3.5 rounded-xl text-left',
          'bg-black/40 border border-white/10',
          'flex items-center justify-between gap-2',
          'transition-all duration-300',
          isFocused && 'border-amber-500/50 shadow-[0_0_20px_rgba(245,158,11,0.15)]',
          !value && 'text-gray-600',
          value && 'text-white'
        )}
      >
        <span className="flex items-center gap-2 truncate">
          {selectedOption?.icon && <span>{selectedOption.icon}</span>}
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-gray-500 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown */}
      <div
        className={cn(
          'absolute z-50 w-full mt-2 py-2 rounded-xl',
          'bg-[#0a0a0a]/95 backdrop-blur-xl',
          'border border-white/10',
          'shadow-[0_20px_60px_rgba(0,0,0,0.8)]',
          'transition-all duration-200 origin-top',
          isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
        )}
      >
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => {
              onChange(option.value);
              setIsOpen(false);
            }}
            className={cn(
              'w-full px-4 py-3 text-left',
              'flex items-center justify-between gap-2',
              'hover:bg-amber-500/10 transition-colors',
              value === option.value ? 'text-amber-500' : 'text-gray-300'
            )}
          >
            <span className="flex items-center gap-3">
              {option.icon && <span className="text-lg">{option.icon}</span>}
              <span>{option.label}</span>
            </span>
            {value === option.value && <Check className="w-4 h-4" />}
          </button>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// MAIN PAGE
// =============================================================================

export default function ContactPage() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    companySize: '',
    currentTools: '',
    budgetRange: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit form');
      }

      setIsSubmitted(true);
    } catch (err) {
      captureClientError(
        err instanceof Error ? err : new Error('Contact form submission failed'),
        'client_runtime',
        { component: 'contact_form' }
      ).catch(() => {});
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  // =========================================================================
  // SUCCESS STATE
  // =========================================================================

  if (isSubmitted) {
    return (
      <div className="min-h-screen text-white" style={{ backgroundColor: '#050505' }}>
        {/* Background */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
          <div className="absolute -top-[20%] -left-[10%] w-[600px] h-[600px] rounded-full bg-green-600/20 blur-[120px]" />
          <div className="absolute -bottom-[20%] -right-[10%] w-[500px] h-[500px] rounded-full bg-green-900/10 blur-[100px]" />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `
                linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)
              `,
              backgroundSize: '48px 48px',
            }}
          />
        </div>

        <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
          <ScrollReveal className="max-w-md w-full text-center">
            <div className="mb-8 inline-flex p-6 rounded-full bg-green-500/10 border border-green-500/30">
              <CheckCircle className="w-16 h-16 text-green-500" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">Transmission Received</h1>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Your enterprise inquiry has been logged in our command center. Our team will analyze
              your requirements and establish contact within 24-48 hours.
            </p>
            <Link
              href="/"
              className={cn(
                'inline-flex items-center gap-2 px-6 py-3 rounded-xl',
                'bg-[#0a0a0a] border border-white/10 text-white',
                'hover:border-amber-500/30 hover:bg-[#111]',
                'transition-all duration-300'
              )}
            >
              <ArrowLeft className="w-4 h-4" />
              Return to Base
            </Link>
          </ScrollReveal>
        </div>
      </div>
    );
  }

  // =========================================================================
  // FORM STATE
  // =========================================================================

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#050505' }}>
      {/* Background atmospheric effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
        {/* Nebula Glow - Top Left */}
        <div className="absolute -top-[20%] -left-[10%] w-[600px] h-[600px] rounded-full bg-amber-600/20 blur-[120px]" />
        {/* Nebula Glow - Bottom Right */}
        <div className="absolute -bottom-[20%] -right-[10%] w-[500px] h-[500px] rounded-full bg-amber-900/10 blur-[100px]" />
        {/* Additional accent */}
        <div className="absolute top-[40%] right-[20%] w-[300px] h-[300px] rounded-full bg-amber-500/5 blur-[80px]" />

        {/* Grid pattern */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)
            `,
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      {/* Content wrapper */}
      <div className="relative" style={{ zIndex: 1 }}>
        {/* Header */}
        <header className="border-b border-white/5 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-amber-500 transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm">Back to Command</span>
            </Link>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pb-32">
          {/* Hero */}
          <ScrollReveal className="text-center mb-16">
            <div
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6',
                'border border-amber-500/30 bg-amber-500/10'
              )}
            >
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span className="text-[10px] text-amber-500 font-mono uppercase tracking-[0.3em] font-bold">
                Enterprise Inquiry
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Scale Your <span className="text-amber-500">Empire</span>
            </h1>
            <p className="text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Tell us about your business and requirements. Our enterprise team will craft a custom
              solution tailored to your operational needs.
            </p>
          </ScrollReveal>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Contact Information */}
            <ScrollReveal delay={100}>
              <GlassCard icon={<Users className="w-5 h-5" />} title="Contact Information">
                <div className="grid md:grid-cols-2 gap-6">
                  <GlassInput
                    label="Full Name"
                    required
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                  />
                  <GlassInput
                    label="Work Email"
                    required
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                  />
                  <GlassInput
                    label="Phone Number"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 000-0000"
                  />
                  <GlassInput
                    label="Company Name"
                    required
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Acme Inc."
                  />
                </div>
              </GlassCard>
            </ScrollReveal>

            {/* Business Details */}
            <ScrollReveal delay={200}>
              <GlassCard icon={<Building2 className="w-5 h-5" />} title="Business Details">
                <div className="grid md:grid-cols-2 gap-6">
                  <GlassSelect
                    label="Company Size"
                    required
                    value={formData.companySize}
                    onChange={(value) => setFormData((prev) => ({ ...prev, companySize: value }))}
                    options={COMPANY_SIZES}
                    placeholder="Select company size"
                  />
                  <GlassSelect
                    label="Monthly Budget"
                    value={formData.budgetRange}
                    onChange={(value) => setFormData((prev) => ({ ...prev, budgetRange: value }))}
                    options={BUDGET_RANGES}
                    placeholder="Select budget range"
                  />
                  <div className="md:col-span-2">
                    <GlassInput
                      label="Current Tools/CRM"
                      name="currentTools"
                      value={formData.currentTools}
                      onChange={handleChange}
                      placeholder="e.g., Salesforce, HubSpot, Spreadsheets, None"
                    />
                  </div>
                </div>
              </GlassCard>
            </ScrollReveal>

            {/* Requirements */}
            <ScrollReveal delay={300}>
              <GlassCard icon={<Wrench className="w-5 h-5" />} title="Your Requirements">
                <GlassTextarea
                  label="Tell us about your needs"
                  required
                  name="message"
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Describe your business challenges, goals, and what you're looking for in a CRM solution. The more detail you provide, the better we can tailor our proposal..."
                />
              </GlassCard>
            </ScrollReveal>

            {/* Error Message */}
            {error && (
              <ScrollReveal>
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  {error}
                </div>
              </ScrollReveal>
            )}

            {/* Submit Button */}
            <ScrollReveal delay={400} className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className={cn(
                  'group relative inline-flex items-center gap-3 px-8 py-4 rounded-xl',
                  'bg-[#1a1a1a] text-white font-bold',
                  'border border-white/10',
                  'hover:border-amber-500/30 hover:bg-[#222]',
                  'shadow-[0_0_30px_rgba(0,0,0,0.4)]',
                  'hover:shadow-[0_0_40px_rgba(245,158,11,0.2)]',
                  'transition-all duration-300',
                  'uppercase tracking-[0.15em] font-mono text-sm',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Transmitting...</span>
                  </>
                ) : (
                  <>
                    <span>Initialize Contact</span>
                    <Send className="w-4 h-4 text-amber-500 group-hover:translate-x-1 group-hover:-translate-y-0.5 transition-transform" />
                  </>
                )}
              </button>
            </ScrollReveal>
          </form>
        </div>
      </div>
    </div>
  );
}
